import { Box, HStack, Link } from '@chakra-ui/react';
import { useUserContext } from 'providers/useUserContext';
import { useEffect, useMemo, useRef } from 'react';
import { Navigate, Route, RouteObject, Routes, useLocation, useRoutes } from 'react-router';
import { NavLink } from 'react-router-dom';
import { ProfileEdit } from 'screens/Account/ProfileEdit';
import { ResetPasswordForm } from 'screens/Account/ResetPasswordForm';
import { ServicesHistory } from 'screens/Account/Services/ServicesHistory';
import { VisitsHistory } from 'screens/Account/Visits/VisitsHistory';
import { Checkout } from 'screens/Checkout';
import { ScaCallback } from 'screens/Checkout/ScaCallback';
import { WalletCallback } from 'screens/Checkout/WalletCallback';
import { ClassScreen } from 'screens/Class';
import { Account } from './screens/Account';
import { Classes } from './screens/Classes';
import { Prices } from './screens/Prices';
import { LandingScreen } from './screens/Landing';
import { useAlmaPaymentProcessor } from 'hooks/useAlmaPaymentProcessor';
import { AlmaPaymentOverlay } from 'components/AlmaPaymentOverlay';
import * as Sentry from '@sentry/react';
import { safeLocalStorage } from 'utils/safeStorage';

function App() {
  const { user } = useUserContext();
  const location = useLocation();
  const { state, processAlmaPayment, cancelPayment } = useAlmaPaymentProcessor();

  // Utiliser useRef pour éviter les appels multiples au même PID
  const processedPaymentIdRef = useRef<string | null>(null);

  // Détecter et traiter les paiements Alma immédiatement
  useEffect(() => {
    // Essayer de récupérer le PID depuis l'URL hash
    const params = new URLSearchParams(location.search);
    let paymentId = params.get('pid');

    // Si pas dans l'URL, vérifier le localStorage
    let recoveredFromStorage = false;
    if (!paymentId) {
      const storedPid = safeLocalStorage.getItem('alma_pending_payment');
      const storedTimestamp = safeLocalStorage.getItem('alma_pending_timestamp');

      if (storedPid && storedTimestamp) {
        // Vérifier que le PID n'est pas trop vieux (max 10 minutes)
        const age = Date.now() - parseInt(storedTimestamp);
        if (age < 10 * 60 * 1000) {
          paymentId = storedPid;
          recoveredFromStorage = true;
        } else {
          safeLocalStorage.removeItem('alma_pending_payment');
          safeLocalStorage.removeItem('alma_pending_timestamp');
        }
      }
    }

    // Vérifier si ce PID a déjà été traité dans cette session
    if (paymentId && processedPaymentIdRef.current !== paymentId) {
      processedPaymentIdRef.current = paymentId;

      // Le pid n'était plus dans la route : le chemin primaire (hash réécrit
      // par main.tsx) a été perdu — le fallback localStorage a pris le relais,
      // c'est un signal à surveiller. (Émis une seule fois par pid grâce au
      // garde processedPaymentIdRef ci-dessus.)
      if (recoveredFromStorage) {
        Sentry.captureMessage('Alma pid recovered from localStorage fallback', {
          level: 'warning',
          tags: {
            category: 'payment',
            paymentMethod: 'alma',
            alma_step: 'pid_recovered_from_storage',
          },
          extra: { paymentId },
        });
      }

      // Déclencher le traitement immédiatement
      processAlmaPayment(paymentId);
    }
  }, [location.search, location.pathname, processAlmaPayment]);

  useEffect(() => {
    if (user) {
      // Id Mindbody seul : pas d'e-mail dans Sentry (même règle que le back,
      // send_default_pii=false).
      Sentry.setUser({ id: user.Id });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // Empêcher la fermeture de la page pendant le processing Alma
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.isProcessing) {
        e.preventDefault();
        e.returnValue = 'Votre paiement est en cours de traitement, ne fermez pas cette page.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.isProcessing]);

  return (
    <Box>
      {/* Overlay Alma global */}
      <AlmaPaymentOverlay
        isOpen={state.isProcessing}
        currentStep={state.currentStep}
        currentAttempt={state.currentAttempt}
        maxAttempts={state.maxAttempts}
        isSlowNetwork={state.isSlowNetwork}
        error={state.error}
        errorStep={state.errorStep}
        onCancel={() => {
          if (state.paymentId) {
            cancelPayment(state.paymentId);
          }
        }}
      />

      <HStack textTransform="uppercase" fontWeight={500} className="mainAppMenu">
        <Link className="mainMenuElement" as={NavLink} to="/">
          Planning
        </Link>
        <Link className="mainMenuElement" as={NavLink} to="tarifs">
          Tarifs
        </Link>
        <Link className="mainMenuElement" as={NavLink} to="account">
          Mon compte
        </Link>
      </HStack>
      <ScrollToTop />
      <Routes>
        <Route index Component={Classes} />
        <Route path="studio/:id" Component={Classes} />
        <Route path="studio/:id/week/:weekNumber" Component={Classes} />
        <Route path="coach/:id" Component={Classes} />
        <Route path="cours/:id" Component={Classes} />
        <Route path="studio/:id/cours/:type" Component={Classes} />
        <Route path="tarifs" Component={Prices} />
        <Route path="event/:slug" Component={LandingScreen} />
        <Route path="class/:id" Component={ClassScreen} />
        <Route path="class/:id/enrollment" Component={ClassScreen} />
        <Route path="account/*" Component={AccountRoutes} />
        <Route path="checkout" Component={Checkout} />
        <Route path="checkout/id/:productId" Component={Checkout} />
        <Route path="checkout/contract/:contractId" Component={Checkout} />
        <Route path="sca-callback" Component={ScaCallback} />
        <Route path="wallet-callback" Component={WalletCallback} />
      </Routes>
    </Box>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AccountRoutes() {
  const { isLoggedIn } = useUserContext();

  const routes = useMemo(() => {
    const routes: RouteObject[] = [
      { index: true, Component: Account },
      { path: 'reset-password', Component: ResetPasswordForm },
      { path: '*', Component: () => Navigate({ to: '/account' }) },
    ];

    if (isLoggedIn) {
      routes.push(
        ...[
          { path: 'edit', Component: ProfileEdit },
          { path: 'visits', Component: VisitsHistory },
          { path: 'services', Component: ServicesHistory },
        ],
      );
    }

    return routes;
  }, [isLoggedIn]);

  return useRoutes(routes);
}

export default App;
